// CSV parsing for the exercise import flows.
import { normalizedCurriculumLabel } from "../domain/curriculum.js";
import { state } from "../state.js";

function parseCSV(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];

    if (inQuotes) {
      if (char === '"') {
        if (next === '"') {
          cell += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      continue;
    }

    if (char === ",") {
      row.push(cell);
      cell = "";
      continue;
    }

    if (char === "\n") {
      row.push(cell);
      if (row.some(value => value.trim())) rows.push(row);
      row = [];
      cell = "";
      continue;
    }

    if (char !== "\r") {
      cell += char;
    }
  }

  row.push(cell);
  if (row.some(value => value.trim())) rows.push(row);
  return rows;
}

export function parseStructuredExerciseCSV(text) {
  const rows = parseCSV(text.trim());
  if (rows.length < 2) {
    throw new Error("CSV file is empty or missing rows.");
  }

  const headers = rows.shift().map(header => header.trim().toLowerCase());
  const usesLearningHierarchy = headers.includes("area_matematica") && headers.includes("argomento");
  const aliases = {
    codice_esercizio: "exercise_code",
    materia: "subject",
    ordine_scolastico_minimo: "min_school_level",
    ordine_scolastico_massimo: "max_school_level",
    area_matematica: "macro_category",
    macro_categoria: "macro_category",
    sotto_categoria: "subcategory",
    argomento: usesLearningHierarchy ? "subcategory" : "curriculum_topic",
    tipologia: "curriculum_topic",
    tag_interno: "curriculum_topic",
    tipologia_esercizio: "exercise_type",
    titolo_esercizio: "title",
    titolo: "title",
    livello_difficolta: "difficulty_level",
    difficolta: "difficulty_level",
    classe: "grade_level",
    classe_minima: "min_grade",
    classe_massima: "max_grade",
    stato: "status",
    domanda: "question",
    risposta_corretta: "correct_answer",
    risposta_errata_1: "wrong_answer_1",
    risposta_errata_2: "wrong_answer_2",
    risposta_errata_3: "wrong_answer_3",
    suggerimento: "hint",
    spiegazione: "explanation",
    lingua: "language"
  };
  const groups = new Map();
  rows.forEach(row => {
    const item = {};
    headers.forEach((header, index) => {
      item[aliases[header] || header] = (row[index] || "").trim();
    });

    const code = item.exercise_code;
    const required = ["macro_category", "subcategory", "title", "question", "correct_answer", "wrong_answer_1", "wrong_answer_2", "wrong_answer_3", "hint", "explanation"];
    if (!code || required.some(key => !item[key])) {
      throw new Error(state.language === "it"
        ? "Ogni riga deve avere codice esercizio e tutti i campi della domanda e delle risposte."
        : "Every row needs an exercise code and all question and answer fields.");
    }
    if (!groups.has(code)) groups.set(code, { ...item, questions: [] });
    groups.get(code).questions.push({
      prompt: item.question,
      answers: [item.correct_answer, item.wrong_answer_1, item.wrong_answer_2, item.wrong_answer_3],
      correct_answer: item.correct_answer,
      hint: item.hint,
      explanation: item.explanation
    });
  });

  return [...groups.values()].map(item => {
    const hierarchyEntry = state.learningHierarchy.find(entry => {
      const area = normalizedCurriculumLabel(item.macro_category);
      const topic = normalizedCurriculumLabel(item.subcategory);
      return (normalizedCurriculumLabel(entry.area_code) === area || normalizedCurriculumLabel(entry.area_name) === area)
        && (normalizedCurriculumLabel(entry.learning_topic_code) === topic || normalizedCurriculumLabel(entry.learning_topic_name) === topic);
    });
    const internalTag = item.curriculum_topic || hierarchyEntry?.default_activity_tag_code || "";
    if (!internalTag) {
      throw new Error(state.language === "it"
        ? `Argomento non riconosciuto: ${item.macro_category} › ${item.subcategory}.`
        : `Unknown learning topic: ${item.macro_category} › ${item.subcategory}.`);
    }
    return ({
      ...(() => {
        const internalGrade = (schoolLevel, grade) => {
          const value = Number(grade || 0);
          return String(schoolLevel || "").toLowerCase().startsWith("med") ? value + 5 : value;
        };
        return {
          min_grade: internalGrade(item.min_school_level, item.min_grade),
          max_grade: internalGrade(item.max_school_level || item.min_school_level, item.max_grade)
        };
      })(),
      exercise_code: item.exercise_code,
      language: (item.language || state.language || "en").toLowerCase().startsWith("it") ? "it" : "en",
      subject: item.subject || "MATH",
      macro_category: item.macro_category || "",
      subcategory: item.subcategory || "",
      curriculum_topic: internalTag,
      exercise_type: item.exercise_type || "MCQ",
      title: item.title,
      difficulty_level: item.difficulty_level || "1",
      grade_level: item.grade_level || "",
      academic_year_id: item.academic_year_id || "",
      status: ["published", "pubblicato"].includes((item.status || "draft").toLowerCase()) ? "Published" : "Draft",
      questions: item.questions
    });
  });
}
